import { Checkbox, Dropdown } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MoreOutlined } from "@ant-design/icons";
import { LocalStorageKey } from "../../../../constants/localStorageKeys";
import { useMapContext } from "../../MapAdapter/core/MapContext";
import { RootState } from "../../../../store/store";
import
{
   flyToGeometry,
   getThumbnail,
   removeFootprint,
   removeImageFromMap,
   showFootprint,
   showImageOnMap,
} from "../../ExploreArchive/ultis";
import Thumbnail from "../../ExploreArchive/ThumbnailToken";
import CommonIcons from "../../../../assets/icons";


const CartImageOrder = ({
   item,
   isSelected,
   toggleItem,
   onDelete,
   handleViewInfo,
   showCheckBox,
   providerCode = "",
   map
}: any) =>
{
   const dispatch = useDispatch();


   const layersOnMap = useSelector((state: RootState) => state.layer.layers);
   const layerIds = useMemo(
      () => new Set(layersOnMap.map((l: any) => l.id)),
      [layersOnMap],
   );

   const isFootprintShown = layerIds.has(`footerprint-${item?.id}`);
   const isImageShown = layerIds.has(`image-${item?.id}`);

   /* ---------------- STATIC CONFIG ---------------- */
   const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
   );

   const baseUrl = staticConfigs?.REACT_APP_API_BASE_URL;

   /* ---------------- ACTIONS ---------------- */

   const menuItems = [
      {
         key: "footprint",
         label: isFootprintShown ? t("Hide footprint") : t("Show footprint"),
         onClick: () =>
         {
            if (isFootprintShown)
            {
               removeFootprint(item, dispatch);
            } else
            {
               showFootprint(item, dispatch, map);
            }
         },
      },
      {
         key: "image",
         label: isImageShown ? t("Hide image on map") : t("Show image on map"),
         onClick: () =>
         {
            if (isImageShown)
            {
               removeImageFromMap(item, dispatch);
            } else
            {
               showImageOnMap(item, dispatch, providerCode, map);
            }
         },
      },
      {
         key: "pan",
         label: t("Pan to"),
         onClick: () =>
         {
            flyToGeometry(item?.geometry, map);
         },
      },
      { key: "view", label: t("Information"), onClick: () => handleViewInfo?.() },

   ];

   /* ---------------- RENDER ---------------- */

   return (
      <div
         onClick={() =>
         {
            if (showCheckBox)
            {
               toggleItem(item)
            }
         }}
         className={`item-card-search !bg-[var(--neutral-900)] mb-2 ${isSelected ? "selected" : ""}`}
      >
         {/* ================== CONTENT ================== */}

         <div className="flex gap-4">
            {/* THUMBNAIL */}
            <div className="w-[80px] h-[80px] rounded bg-[#0b1323] relative">
               <Thumbnail
                  url={getThumbnail(item?.assets, baseUrl)}
                  token={localStorage.getItem("token") ?? ""}
               />
               {
                  showCheckBox && (<div className="absolute top-1 left-1">
                     <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                        {
                           e.stopPropagation();
                           toggleItem(item);
                        }}
                     />
                  </div>)
               }


            </div>

            {/* INFO */}

            <div className="flex-1">
               <div className="flex justify-between">
                  <div>
                     <div className="text-xs text-gray-400">
                        {item?.collection ?? item.properties?.platform}
                     </div>

                     <div className="text-sm font-medium mb-2">{item?.id}</div>
                  </div>

                  <Dropdown
                     menu={{
                        items: menuItems,
                        onClick: (e) =>
                        {
                           e.domEvent.stopPropagation();
                        },
                     }}
                     trigger={["click"]}
                  >
                     <div onClick={(e) => e.stopPropagation()}>
                        <MoreOutlined className="text-gray-400" />
                     </div>
                  </Dropdown>
               </div>

               <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-300">
                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.DatetimeIcon /> {t("Date time")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.datetime
                           ? dayjs(item.properties?.datetime).format("DD/MM/YYYY")
                           : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Cloud cover")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["eo:cloud_cover"]
                           ? item.properties?.["eo:cloud_cover"]?.toFixed(2)
                           : "---"}
                        %
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.CloudIcon /> {t("Incidence angle")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.["view:off_nadir"]
                           ? item.properties?.["view:off_nadir"]?.toFixed(2)
                           : "---"}
                     </span>
                  </div>

                  <div className="flex gap-2">
                     <span className="flex gap-1">
                        <CommonIcons.GsdIcon /> {t("Resolution")}:
                     </span>
                     <span className="text-[var(--color-main-text)]">
                        {item.properties?.gsd
                           ? item.properties?.gsd?.toFixed(2)
                           : "---"}
                     </span>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default CartImageOrder;
